import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Sparkles } from "lucide-react";
import { QUOTE_URL } from "@/lib/constants";
import { useSiteContent } from "@/lib/content";

const lineWrap = {
  hidden: {},
  show: { transition: { staggerChildren: 0.16, delayChildren: 0.35 } },
};
const line = {
  hidden: { y: "115%" },
  show: { y: "0%", transition: { duration: 1, ease: [0.22, 1, 0.36, 1] } },
};

export default function Hero() {
  const content = useSiteContent();
  const { scrollY } = useScroll();
  const yLeft = useTransform(scrollY, [0, 700], [0, 140]);
  const yRight = useTransform(scrollY, [0, 700], [0, 200]);
  const fade = useTransform(scrollY, [0, 500], [1, 0]);

  return (
    <section id="top" data-testid="hero-section" className="relative flex min-h-screen flex-col items-center justify-center px-4 pt-24 pb-16 overflow-hidden">
      <motion.img
        src="/brand/floral-tl.png"
        alt=""
        aria-hidden
        style={{ y: yLeft }}
        className="pointer-events-none absolute -left-10 -top-10 w-56 sm:w-80 opacity-90"
      />
      <motion.img
        src="/brand/floral-br.png"
        alt=""
        aria-hidden
        style={{ y: yRight }}
        className="pointer-events-none absolute -right-10 -bottom-16 w-56 sm:w-96 opacity-90"
      />

      <motion.div style={{ opacity: fade }} className="relative z-10 flex flex-col items-center text-center">
        <motion.div
          initial={{ scale: 0, rotate: -12 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ type: "spring", stiffness: 120, damping: 14, delay: 0.1 }}
          className="relative mb-8 animate-float-slow"
        >
          <img
            src="/brand/logo.png"
            alt="Miss Cupcake Creations cupcake logo"
            data-testid="hero-logo"
            className="h-36 w-36 sm:h-44 sm:w-44 rounded-full ring-2 ring-gold/60 shadow-[0_24px_60px_-16px_rgba(123,45,78,0.45)]"
          />
          <Sparkles className="absolute -right-2 -top-1 h-6 w-6 text-gold animate-sparkle" />
          <Sparkles className="absolute -left-3 bottom-4 h-4 w-4 text-gold/70 animate-sparkle [animation-delay:1.2s]" />
        </motion.div>

        <motion.p
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25, duration: 0.8 }}
          className="font-script text-2xl sm:text-3xl text-gold"
          data-testid="hero-eyebrow"
        >
          {content?.text?.hero_eyebrow || "Everyone celebrates something"}
        </motion.p>

        <motion.h1
          variants={lineWrap}
          initial="hidden"
          animate="show"
          className="mt-3 font-serif font-semibold tracking-tight text-burgundy text-5xl sm:text-6xl lg:text-7xl leading-[1.02]"
          data-testid="hero-heading"
        >
          <span className="block overflow-hidden pb-1">
            <motion.span variants={line} className="block">Miss Cupcake</motion.span>
          </span>
          <span className="block overflow-hidden pb-2">
            <motion.span variants={line} className="block">Creations</motion.span>
          </span>
        </motion.h1>

        <motion.div
          initial={{ scaleX: 0, opacity: 0 }}
          animate={{ scaleX: 1, opacity: 1 }}
          transition={{ delay: 1.05, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="mt-6 flex w-64 items-center gap-3"
          aria-hidden
        >
          <span className="ornament-line flex-1" />
          <span className="h-2 w-2 rotate-45 bg-gold" />
          <span className="ornament-line flex-1" />
        </motion.div>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.15, duration: 0.8 }}
          className="mt-5 font-script text-3xl sm:text-4xl text-gold"
          data-testid="hero-tagline"
        >
          {content?.text?.hero_tagline || "Celebrate it uniquely"}
        </motion.p>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.35, duration: 0.8 }}
          className="mt-4 text-sm sm:text-base text-wine/80 tracking-wide"
        >
          Custom Cakes <span className="text-gold">•</span> Cupcakes <span className="text-gold">•</span> Cookies
          <span className="mx-2 text-gold/60">|</span> Hillcrest / Logan
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.5, duration: 0.8 }}
          className="mt-9 flex flex-wrap items-center justify-center gap-4"
        >
          <a
            href={QUOTE_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-quote-button"
            className="rounded-full border border-gold bg-gold px-8 py-3.5 text-sm font-semibold uppercase tracking-[0.18em] text-white shadow-[0_14px_36px_-10px_rgba(201,162,39,0.8)] transition-all duration-300 hover:-translate-y-1 hover:bg-golddeep"
          >
            Request a Quote
          </a>
          <a
            href="#gallery"
            data-testid="hero-gallery-button"
            className="rounded-full border border-burgundy/40 bg-white/70 px-8 py-3.5 text-sm font-semibold uppercase tracking-[0.18em] text-burgundy backdrop-blur transition-all duration-300 hover:-translate-y-1 hover:border-gold hover:text-golddeep"
          >
            Browse the Gallery
          </a>
        </motion.div>
      </motion.div>

      <motion.a
        href="#gallery"
        aria-label="Scroll to gallery"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2 }}
        className="absolute bottom-8 z-10 text-burgundy/60 hover:text-gold transition-colors"
        data-testid="hero-scroll-cue"
      >
        <ArrowDown className="h-5 w-5 animate-bounce" />
      </motion.a>
    </section>
  );
}
